import { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from '@/hooks'
import { useFormik } from 'formik'
import { UseFormRegister } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { utilityAccountsCollection } from '@/reducers/utilityAccounts'
import useApiDispatch from '@/hooks/useApiDispatch/useApiDispatch'
import { programsNewCollection } from '@/reducers/programsNew'
import { AccountFieldKey, AccountFieldsConfig } from '@/types/utilityProgram'
import {
  buildAccountFieldsValidationSchema,
  buildInitialValues,
  buildPatchPayload,
  useAccountFieldCmsOverrides,
  useUtilityBillUpload,
} from '@/app/features/utility-account-fields'

export type AccountNumberEntryProps = {
  onFinish: () => void
}

type AccountFieldValues = Record<AccountFieldKey, string>

/**
 * Base set of fields on the Profile → Utility Account page: all three, all
 * optional (the user can fill in whatever they have and Skip or save with Next).
 * Labels and validation lengths are layered on from the relevant program's CMS
 * config — see {@link useAccountFieldCmsOverrides}.
 */
const PROFILE_ACCOUNT_FIELDS: AccountFieldsConfig = [
  {
    field: 'account_number',
    required: false,
    label: '',
    validationLength: null,
  },
  {
    field: 'contract_number',
    required: false,
    label: '',
    validationLength: null,
  },
  { field: 'meter_number', required: false, label: '', validationLength: null },
]

export default function useViewModel({ onFinish }: AccountNumberEntryProps) {
  const {
    utilityWebsite,
    home,
    isLoading: fetchingUser,
  } = useAppSelector((state) => ({
    // Prefer the utility selected during the connect flow; fall back to the
    // profile's utility so the website link still resolves on direct entry.
    utilityWebsite:
      state.utilities?.selectedUtility?.website ??
      state.user?.user?.profile?.utility?.website ??
      null,
    isLoading: Boolean(state.user?.isFetchingUser),
    home: state.homes?.selectedHome,
  }))
  const { apiDispatch } = useApiDispatch()
  const dispatch = useDispatch()
  const [submissionErrors, setSubmissionErrors] = useState<string | null>(null)

  // Layer the relevant program's CMS labels + validation lengths onto the fields.
  const overrides = useAccountFieldCmsOverrides()
  const config = useMemo<AccountFieldsConfig>(
    () =>
      PROFILE_ACCOUNT_FIELDS.map((f) => ({
        ...f,
        label: overrides[f.field].label,
        validationLength: overrides[f.field].validationLength,
      })),
    [overrides],
  )
  const validationSchema = useMemo(
    () => buildAccountFieldsValidationSchema(config),
    [config],
  )

  const { isLoadingOrIdle: fetchingUtility, data } =
    utilityAccountsCollection.useFetch('/utilities/utility_account')
  const savedAccount = data?.[0]

  const upload = useUtilityBillUpload({ config })

  const formik = useFormik<AccountFieldValues>({
    enableReinitialize: true,
    validateOnMount: true,
    // Seed empty fields from a previously-uploaded bill's extraction.
    initialValues: buildInitialValues(
      config,
      savedAccount,
      undefined,
      upload.persistedExtraction,
    ),
    validationSchema,
    onSubmit: () => {
      submit()
    },
  })

  // Fields are always driven by the saved utility account. A bill uploaded on
  // this page autofills them as a convenience; an extraction stored from an
  // earlier upload only feeds the "Extracted from bill" label (below).
  useEffect(() => {
    const entries = Object.entries(upload.extractedValues) as [
      AccountFieldKey,
      string,
    ][]
    if (entries.length === 0) {
      return
    }
    entries.forEach(([field, value]) =>
      formik.setFieldValue(field, value, true),
    )
  }, [upload.extractedValues])

  // "Extracted from bill" label values: the persisted extraction, overridden by
  // anything just extracted from a bill uploaded on this page. Display only.
  const extractedValues = {
    ...upload.persistedExtraction,
    ...upload.extractedValues,
  }

  const register = ((name: string) => {
    return {
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange(event)
      },
      onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
        formik.handleBlur(event)
      },
      name,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ref: (_element: HTMLInputElement | null) => {
        // No-op ref for formik compatibility
      },
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }) as unknown as UseFormRegister<any>

  // Every field is optional, but Next stays disabled until the user has entered
  // (or extracted) at least one value, and any filled field must pass validation
  // (e.g. its length check). Skip is always available.
  const canProceed =
    formik.isValid &&
    config.some((f) => (formik.values[f.field] ?? '').trim().length > 0) &&
    !formik.isSubmitting

  const submit = async () => {
    if (!canProceed) {
      return
    }
    const response = await apiDispatch(
      utilityAccountsCollection.actions.update(
        `/utilities/utility_account/${home?.id}`,
        buildPatchPayload(config, formik.values),
        { id: home?.id },
      ),
    )

    if (response.ok) {
      // Account changes may affect program eligibility shown elsewhere.
      dispatch(programsNewCollection.actions.invalidate())
      onFinish()
    } else {
      setSubmissionErrors(response.error.message)
    }
  }

  const skip = () => {
    onFinish()
  }

  const isLoading = fetchingUtility || fetchingUser

  return {
    isLoading,
    config,
    upload,
    extractedValues,
    formik,
    register,
    canProceed,
    submit,
    skip,
    submissionErrors,
    utilityWebsite,
  }
}
